import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Check } from "lucide-react";
import { PageHero } from "../components/ui-kit";
import { Reveal } from "../motion";
import { PARTNERS_CONTACT, OFFICE, MAILTO } from "../content";

const STEPS = [
  "Leitura preliminar da descrição.",
  "Verificação de disponibilidade e conflito de interesses.",
  "Definição do profissional e do canal para continuidade.",
  "Orientação sobre os documentos necessários.",
  "Definição de escopo e formalização, caso o atendimento seja aceito.",
];

const field = "w-full bg-white border border-ink/15 rounded-lg px-4 py-3 text-ink placeholder-ink/35 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";

export default function Contato() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", subject: "", deadline: "", message: "" });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Preencha nome, e-mail e a mensagem.");
      return;
    }
    const subject = `Contato pelo site — ${form.company || form.name}`;
    const lines = [
      `Nome: ${form.name}`,
      form.company && `Empresa: ${form.company}`,
      `E-mail: ${form.email}`,
      form.phone && `Telefone: ${form.phone}`,
      form.subject && `Assunto: ${form.subject}`,
      form.deadline && `Prazo/Intimação: ${form.deadline}`,
      "",
      "Mensagem:",
      form.message,
    ].filter((l) => l !== false && l !== undefined && l !== null);
    const href = `mailto:${OFFICE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
    window.location.href = href;
    setSent(true);
    toast.success("Abrindo seu aplicativo de e-mail para enviar a mensagem.");
  };

  return (
    <>
      <PageHero
        eyebrow="Contato"
        titleLines={["Fale com o", "escritório."]}
        lead="Uma descrição breve do assunto, das pessoas ou empresas envolvidas e de eventual prazo permite verificar disponibilidade, possíveis conflitos de interesses e o canal adequado para prosseguir."
      />
      <section className="bg-bone py-20 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-20">
          <Reveal>
            {sent ? (
              <div data-testid="contact-success" className="bg-white border border-gold/40 rounded-2xl p-12 text-center">
                <div className="h-16 w-16 rounded-full bg-gold/15 text-gold grid place-items-center mx-auto mb-6"><Check size={30} /></div>
                <h2 className="serif text-3xl text-ink mb-3">Quase lá</h2>
                <p className="text-ink/60 font-light max-w-md mx-auto">Abrimos seu aplicativo de e-mail com a mensagem pronta — basta enviá-la para {OFFICE.email}. Se preferir, fale diretamente pelo WhatsApp dos sócios ao lado.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-ink/10 rounded-2xl p-8 lg:p-10">
                <div className="eyebrow text-gold mb-6">Formulário de contato</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="input-name" className={field} placeholder="Nome*" value={form.name} onChange={set("name")} />
                  <input data-testid="input-company" className={field} placeholder="Empresa (se aplicável)" value={form.company} onChange={set("company")} />
                  <input data-testid="input-email" type="email" className={field} placeholder="E-mail*" value={form.email} onChange={set("email")} />
                  <input data-testid="input-phone" className={field} placeholder="Telefone para retorno" value={form.phone} onChange={set("phone")} />
                  <input data-testid="input-subject" className={`${field} sm:col-span-2`} placeholder="Assunto em poucas linhas" value={form.subject} onChange={set("subject")} />
                  <input data-testid="input-deadline" className={`${field} sm:col-span-2`} placeholder="Existe prazo ou intimação?" value={form.deadline} onChange={set("deadline")} />
                  <textarea data-testid="input-message" rows={5} className={`${field} sm:col-span-2 resize-none`} placeholder="Descreva brevemente a questão*" value={form.message} onChange={set("message")} />
                </div>
                <p className="text-ink/45 text-xs mt-4 leading-relaxed">Não envie documentos sigilosos ou dados sensíveis antes da confirmação de atendimento. O envio da mensagem não constitui automaticamente relação advogado-cliente.</p>
                <button data-testid="contact-submit" className="btn-gold mt-6 w-full sm:w-auto justify-center">
                  Enviar mensagem
                </button>
              </form>
            )}
          </Reveal>

          <div className="space-y-6">
            <Reveal className="bg-ink text-bone rounded-2xl p-8 grain overflow-hidden">
              <div className="eyebrow text-gold mb-3">E-mail institucional</div>
              <p className="text-bone/60 text-sm font-light mb-4">Canal principal para contextualizar a questão por escrito.</p>
              <a href={MAILTO} data-testid="contact-email-link" className="inline-flex items-center gap-2 text-gold link-underline"><Mail size={16} /> {OFFICE.email}</a>
            </Reveal>
            {[PARTNERS_CONTACT.lucas, PARTNERS_CONTACT.rodrigo].map((p) => (
              <Reveal key={p.name} className="bg-white border border-ink/10 rounded-2xl p-7">
                <div className="serif text-xl text-ink">{p.name}</div>
                <div className="text-ink/50 text-sm mb-4">{p.oab} · {p.phone}</div>
                <a href={p.wa} target="_blank" rel="noreferrer" data-testid={`contact-wa-${p.name.split(" ")[0].toLowerCase()}`} className="inline-flex items-center gap-1.5 text-ink font-medium link-underline">WhatsApp <ArrowUpRight size={15} className="text-gold" /></a>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 mt-20 border-t border-ink/12 pt-12">
          <div className="eyebrow text-gold mb-8">O que acontece após o primeiro contato</div>
          <div className="grid md:grid-cols-5 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="serif text-gold text-3xl mb-3">0{i + 1}</div>
                <p className="text-ink/65 text-sm leading-relaxed">{s}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
